import { Car, Fuel, MapPin, Users } from "lucide-react";
import type { FuelChartDatum } from "@/lib/fuel-analytics";
import { cn } from "@/lib/cn";
import { formatCurrency } from "@/lib/utils";

type RankingKind = "users" | "stations" | "vehicles";

type Props = {
  title: string;
  entries: FuelChartDatum[];
  kind: RankingKind;
  aggregateByName?: boolean;
};

function aggregateEntries(entries: FuelChartDatum[]): FuelChartDatum[] {
  const totals = new Map<string, number>();

  for (const entry of entries) {
    totals.set(entry.name, (totals.get(entry.name) ?? 0) + entry.value);
  }

  return [...totals.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function rankBadgeClass(rank: number): string {
  if (rank === 1) return "bg-accent text-surface";
  if (rank === 2) return "bg-foreground/85 text-surface";
  if (rank === 3) return "bg-foreground/55 text-surface";
  return "bg-canvas text-muted";
}

function kindIcon(kind: RankingKind) {
  if (kind === "users") return Users;
  if (kind === "vehicles") return Car;
  return MapPin;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || "?";
}

export function FuelSpendRankingPanel({ title, entries, kind, aggregateByName = false }: Props) {
  const ranked = aggregateByName ? aggregateEntries(entries) : [...entries].sort((a, b) => b.value - a.value);
  const rows = ranked.filter((entry) => entry.value > 0).slice(0, 8);

  if (rows.length === 0) return null;

  const totalSpent = rows.reduce((sum, row) => sum + row.value, 0);
  const maxSpent = Math.max(...rows.map((row) => row.value), 1);
  const topEntry = rows[0];
  const Icon = kindIcon(kind);

  return (
    <div className="flex h-full flex-col rounded-3xl bg-canvas p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">{title}</p>
          <p className="mt-0.5 text-xs text-muted">
            {rows.length} {rows.length === 1 ? "entry" : "entries"} · {formatCurrency(totalSpent)} total
          </p>
        </div>
        {topEntry ? (
          <span className="shrink-0 rounded-full bg-accent-subtle px-2.5 py-1 text-[10px] font-bold text-accent-foreground">
            Top · {topEntry.name}
          </span>
        ) : null}
      </div>

      <div className="stagger-children mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto pr-0.5">
        {rows.map((row, index) => {
          const rank = index + 1;
          const isTop = rank === 1;
          const spendShare = totalSpent ? Math.round((row.value / totalSpent) * 100) : 0;
          const barWidth = Math.max(8, Math.round((row.value / maxSpent) * 100));

          return (
            <div
              key={`${row.name}-${rank}`}
              className={cn(
                "rounded-2xl border border-transparent bg-surface p-3 shadow-[0_1px_0_rgba(0,0,0,0.03)] transition-shadow duration-300 hover:shadow-[var(--shadow-elevation)]",
                isTop && "border-accent/20 bg-accent-subtle/40"
              )}
            >
              <div className="flex items-start gap-3">
                <div className="flex shrink-0 flex-col items-center gap-1.5">
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-extrabold",
                      rankBadgeClass(rank)
                    )}
                  >
                    {rank}
                  </span>
                  {kind === "users" ? (
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-canvas text-xs font-bold text-accent-foreground">
                      {initials(row.name)}
                    </span>
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-canvas text-muted">
                      <Icon className="h-4 w-4" />
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-foreground">{row.name}</p>
                      <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-muted">
                        {kind === "stations" ? (
                          <>
                            <Fuel className="h-3.5 w-3.5 shrink-0" />
                            Station spend
                          </>
                        ) : kind === "vehicles" ? (
                          <>
                            <Car className="h-3.5 w-3.5 shrink-0" />
                            Vehicle spend
                          </>
                        ) : (
                          <>
                            <Users className="h-3.5 w-3.5 shrink-0" />
                            User spend
                          </>
                        )}
                      </p>
                    </div>
                    <p className="shrink-0 text-right text-sm font-extrabold tracking-tight text-foreground">
                      {formatCurrency(row.value)}
                    </p>
                  </div>

                  <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-canvas">
                    <div
                      className={cn("grow-bar h-full rounded-full", isTop ? "bg-accent" : "bg-foreground/75")}
                      style={{ width: `${barWidth}%`, animationDelay: `${index * 60}ms` }}
                    />
                  </div>

                  <p className="mt-1.5 text-[10px] font-semibold text-muted">{spendShare}% of panel total</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
